import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  ClipboardList,
  CreditCard,
  ExternalLink,
  Timer,
} from "lucide-react";
import { navigate } from "raviger";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { getCurrencySymbol } from "@/components/ui/monetary-display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import mutate from "@/Utils/request/mutate";
import query, { callApi } from "@/Utils/request/query";
import { dateQueryString } from "@/Utils/utils";
import { AppointmentSlotPicker } from "@/pages/Appointments/BookAppointment/AppointmentSlotPicker";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import { getBasePrice } from "@/types/base/monetaryComponent/monetaryComponent";
import {
  AccountBillingStatus,
  AccountStatus,
} from "@/types/billing/account/Account";
import accountApi from "@/types/billing/account/accountApi";
import { InvoiceStatus } from "@/types/billing/invoice/invoice";
import invoiceApi from "@/types/billing/invoice/invoiceApi";
import { EncounterListRead } from "@/types/emr/encounter/encounter";
import encounterApi from "@/types/emr/encounter/encounterApi";
import { Status } from "@/types/emr/serviceRequest/serviceRequest";
import serviceRequestApi from "@/types/emr/serviceRequest/serviceRequestApi";
import { TagConfig } from "@/types/emr/tagConfig/tagConfig";
import scheduleApi from "@/types/scheduling/scheduleApi";

import { ScheduleResourceFormState } from "@/components/Schedule/ResourceSelector";
import {
  Appointment,
  AppointmentActionResponse,
  AppointmentStatus,
  AppointmentVisitType,
  PatientVisitType,
  SchedulableResourceType,
  TokenSlot,
} from "@/types/scheduling/schedule";
import { AppointmentDateSelection } from "./AppointmentDateSelection";
import { AppointmentFormSection } from "./AppointmentFormSection";

/**
 * OPD Appointment Payment Gate -- v3 (inline fee card)
 *
 * When OPD is selected and the schedule has a consultation fee, a fee card
 * appears below the slot picker. Clicking "Create Invoice":
 *  1. Creates a 'pending' appointment (holds the slot for 5 minutes).
 *  2. Stores state in sessionStorage.
 *  3. Navigates to the patient billing page to create/pay the invoice.
 * On return the slot is shown as "Reserved - X:XX" in the picker.
 * Once the fee is paid, "Confirm Appointment" is enabled.
 * If the 5-minute timer expires the slot is released automatically.
 */

const PENDING_BOOKING_STORAGE_KEY = (facilityId: string, patientId: string) =>
  `opd_pending_booking:${facilityId}:${patientId}`;

type PersistedState = {
  pendingAppointmentId: string;
  pendingExpiry: string;
  slotId: string;
  slot: TokenSlot;
  date: string;
  resource: unknown;
  resourceId: string;
  resourceType: SchedulableResourceType;
  accountId: string | null;
  invoiceId: string | null;
  encounterId: string | null;
  serviceRequestId: string | null;
};

type PendingCancelRequest = { reason: "cancelled" | "entered_in_error" };

export const BookAppointmentDetails = ({
  patientId,
  onSuccess,
}: {
  patientId: string;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation();
  const { facilityId } = useCurrentFacility();
  const storageKey = PENDING_BOOKING_STORAGE_KEY(facilityId, patientId);
  const [showEncounterDialog, setShowEncounterDialog] = useState(false);
  const [activeEncounters, setActiveEncounters] = useState<EncounterListRead[]>(
    [],
  );
  const [selectedEncounter, setSelectedEncounter] =
    useState<EncounterListRead | null>(null);
  const [pendingBillingFlow, setPendingBillingFlow] = useState(false);

  const persistedState = useMemo((): PersistedState | null => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as PersistedState) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  // Form state
  const [selectedResource, setSelectedResource] =
    useState<ScheduleResourceFormState>(() => {
      const rType =
        persistedState?.resourceType ?? SchedulableResourceType.Practitioner;
      if (persistedState?.resource) {
        return {
          resource: persistedState.resource,
          resource_type: rType,
        } as unknown as ScheduleResourceFormState;
      }
      return { resource: null, resource_type: rType };
    });
  const [selectedDate, setSelectedDate] = useState<Date>(() =>
    persistedState?.date ? new Date(persistedState.date) : new Date(),
  );
  const [selectedSlotId, setSelectedSlotId] = useState<string | undefined>(
    persistedState?.slotId,
  );
  const [selectedSlot, setSelectedSlot] = useState<TokenSlot | null>(
    persistedState?.slot ?? null,
  );
  const [selectedServiceRequestId, setSelectedServiceRequestId] = useState<
    string | null
  >(persistedState?.serviceRequestId ?? null);
  const [selectedTags, setSelectedTags] = useState<TagConfig[]>([]);
  const [reason, setReason] = useState("");
  const [selectedVisitType, setSelectedVisitType] = useState(
    AppointmentVisitType.OPD,
  );
  const [selectedPatientVisitType, setSelectedPatientVisitType] = useState(
    PatientVisitType.ROUTINE,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerStep, setDrawerStep] = useState<1 | 2>(1);

  // Stores the page URL at the moment "Create Invoice" is clicked, so the invoice page
  // can navigate back here (and auto-reopen the booking sheet) via its sourceUrl param.
  const pendingReturnUrlRef = useRef<string>("");

  // Stable ref so cancel mutation always reads the latest pendingBookingId at
  // call-time rather than capturing a stale closure value at render-time.
  const pendingBookingIdRef = useRef<string | null>(null);
  const hasAutoConfirmedRef = useRef(false);

  // Pending appointment reservation state
  const [pendingBookingId, setPendingBookingId] = useState<string | null>(
    persistedState?.pendingAppointmentId ?? null,
  );
  pendingBookingIdRef.current = pendingBookingId;
  const [pendingExpiry, setPendingExpiry] = useState<Date | null>(
    persistedState?.pendingExpiry
      ? new Date(persistedState.pendingExpiry)
      : null,
  );
  const [pendingInvoiceId, setPendingInvoiceId] = useState<string | null>(
    persistedState?.invoiceId ?? null,
  );
  const [pendingEncounterId, setPendingEncounterId] = useState<string | null>(
    persistedState?.encounterId ?? null,
  );
  const [invoiceNumberRetry, setInvoiceNumberRetry] = useState<{
    invoiceId: string;
    chargeItemId?: string;
    openDiscountEditor: boolean;
  } | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // Poll the pending appointment to detect payment
  const { data: pendingAppointment, isError: isPendingAppointmentError } =
    useQuery({
      queryKey: ["pending-appointment", facilityId, pendingBookingId],
      queryFn: query(scheduleApi.appointments.retrieve, {
        pathParams: { facilityId, id: pendingBookingId! },
        silent: true,
      }),
      enabled: !!pendingBookingId,
      refetchInterval: 5000,
    });

  const { data: defaultAccount, refetch: refetchDefaultAccount } = useQuery({
    queryKey: ["default-account", facilityId, patientId],
    queryFn: query(accountApi.listAccount, {
      pathParams: { facilityId },
      queryParams: {
        patient: patientId,
        limit: 1,
        offset: 0,
        status: AccountStatus.active,
        billing_status: AccountBillingStatus.open,
      },
      silent: true,
    }),
  });

  const { data: encounterData } = useQuery({
    queryKey: ["active-encounters", patientId, facilityId],
    queryFn: query(encounterApi.activeEncounter, {
      queryParams: {
        patient: patientId,
        facility: facilityId,
      },
    }),
    enabled: !!patientId && !!facilityId,
  });

  useEffect(() => {
    const active = encounterData?.results ?? [];

    setActiveEncounters(active);

    if (active.length === 1) {
      setSelectedEncounter(active[0]);
    } else {
      setSelectedEncounter(null);
    }
  }, [encounterData]);

  const defaultAccountId = defaultAccount?.results[0]?.id ?? null;
  const selectedResourceId = selectedResource.resource?.id;
  const requiresServiceRequestLink =
    !!selectedResource.resource &&
    selectedResource.resource_type !== SchedulableResourceType.Practitioner;
  const serviceRequestQueryParams = useMemo(() => {
    if (!requiresServiceRequestLink || !selectedResourceId) {
      return null;
    }
    return {
      patient: patientId,
      appointment_isnull: true,
      limit: 50,
      offset: 0,
      ...(selectedResource.resource_type === SchedulableResourceType.Location
        ? { location: selectedResourceId }
        : {}),
      ...(selectedResource.resource_type ===
      SchedulableResourceType.HealthcareService
        ? { healthcare_service: selectedResourceId }
        : {}),
    };
  }, [
    patientId,
    requiresServiceRequestLink,
    selectedResource.resource_type,
    selectedResourceId,
  ]);

  const { data: serviceRequestData, isLoading: isLoadingServiceRequests } =
    useQuery({
      queryKey: [
        "appointment-service-requests",
        facilityId,
        serviceRequestQueryParams,
      ],
      queryFn: query(serviceRequestApi.listServiceRequest, {
        pathParams: { facilityId },
        queryParams: serviceRequestQueryParams ?? {},
      }),
      enabled: !!serviceRequestQueryParams,
    });

  const eligibleServiceRequests = useMemo(() => {
    return (serviceRequestData?.results ?? []).filter((serviceRequest) =>
      [Status.draft, Status.active, Status.on_hold].includes(
        serviceRequest.status,
      ),
    );
  }, [serviceRequestData?.results]);
  const selectedServiceRequest = eligibleServiceRequests.find(
    (serviceRequest) => serviceRequest.id === selectedServiceRequestId,
  );

  // If the pending appointment is not found (e.g. expired/deleted), clear the reservation.
  useEffect(() => {
    if (isPendingAppointmentError && pendingBookingId) {
      clearReservation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingAppointmentError]);

  // Countdown timer
  useEffect(() => {
    if (!pendingExpiry) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const left = Math.max(
        0,
        Math.floor((pendingExpiry.getTime() - Date.now()) / 1000),
      );
      setSecondsLeft(left);
      return left;
    };
    tick();
    const id = setInterval(() => {
      if (tick() === 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [pendingExpiry]);

  // When polling detects the appointment was cancelled by the backend (e.g. expiry
  // cleanup job ran), clear the local reservation so the UI resets cleanly.
  useEffect(() => {
    if (
      pendingAppointment?.status === AppointmentStatus.CANCELLED &&
      pendingBookingId
    ) {
      clearReservation();
      toast.warning(t("slot_reservation_expired"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAppointment?.status]);

  // Auto-cancel the pending appointment when the client-side timer hits zero.
  // This immediately frees the slot on the backend without waiting for the
  // server-side cleanup job.
  useEffect(() => {
    if (secondsLeft !== 0 || !pendingBookingIdRef.current) return;
    cancelBookingMutation({ reason: "cancelled" }).catch(() => {});
    toast.warning(t("slot_reservation_expired"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  const timerLabel =
    secondsLeft !== null && secondsLeft > 0
      ? `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(secondsLeft % 60).padStart(2, "0")}`
      : null;

  const clearReservation = () => {
    sessionStorage.removeItem(storageKey);
    setPendingBookingId(null);
    setPendingInvoiceId(null);
    setPendingEncounterId(null);
    setPendingExpiry(null);
    setSecondsLeft(null);
    setSelectedSlotId(undefined);
    setSelectedSlot(null);
    hasAutoConfirmedRef.current = false;
  };

  const savePendingState = (
    appointment: Appointment,
    invoiceId: string | null = null,
    accountId: string | null = defaultAccountId,
    encounterId: string | null = null,
  ) => {
    const expiryDate = appointment.pending_expiry
      ? new Date(appointment.pending_expiry)
      : new Date(Date.now() + 5 * 60 * 1000);
    const state: PersistedState = {
      pendingAppointmentId: appointment.id,
      pendingExpiry: expiryDate.toISOString(),
      slotId: selectedSlotId!,
      slot: selectedSlot!,
      date: dateQueryString(selectedDate),
      resource: selectedResource.resource,
      resourceId: selectedResource.resource?.id ?? "",
      resourceType: selectedResource.resource_type,
      accountId,
      invoiceId,
      encounterId,
      serviceRequestId: selectedServiceRequestId,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(state));
    setPendingBookingId(appointment.id);
    setPendingInvoiceId(invoiceId);
    setPendingEncounterId(encounterId);
    setPendingExpiry(expiryDate);
  };

  const resolveDefaultAccountId = async () =>
    defaultAccountId ??
    (await refetchDefaultAccount()).data?.results[0]?.id ??
    null;

  // Derived flags
  const consultationFee =
    selectedSlot?.availability.charge_item_definition ?? null;
  // Appointment fees are collected during check-in. Keep the legacy
  // reservation/payment branches inactive so scheduling never creates a
  // pre-arrival invoice or blocks appointment creation.
  const isBeforeAppointmentGate = false;
  const isSelectedSlotResolved =
    !selectedSlotId ||
    selectedSlot?.id === selectedSlotId ||
    (!!pendingBookingId && selectedSlot?.id === selectedSlotId);
  const isResolvingOpdSlot =
    isBeforeAppointmentGate &&
    selectedVisitType === AppointmentVisitType.OPD &&
    !!selectedSlotId &&
    !pendingBookingId &&
    !isSelectedSlotResolved;
  const showFeeCard =
    isBeforeAppointmentGate &&
    selectedVisitType === AppointmentVisitType.OPD &&
    (!!consultationFee || !!pendingBookingId || isResolvingOpdSlot);
  const isPaid = pendingAppointment?.payment_gate?.paid === true;
  const isTimerExpired = secondsLeft === 0 && secondsLeft !== null;
  const isReservationActive = !!pendingBookingId && !isTimerExpired;
  const hasPendingInvoice =
    !!pendingInvoiceId || !!pendingAppointment?.charge_item?.paid_invoice;
  const isSlotSelectionLocked = isReservationActive && hasPendingInvoice;

  const canConfirm =
    !!selectedSlotId &&
    (selectedVisitType !== AppointmentVisitType.OPD ||
      !isBeforeAppointmentGate ||
      (isSelectedSlotResolved && !consultationFee) ||
      (isReservationActive && isPaid));

  // Mutations
  const { mutateAsync: createAppointmentMutation, isPending: isCreating } =
    useMutation({
      mutationFn: mutate(scheduleApi.slots.createAppointment, {
        pathParams: { facilityId, slotId: selectedSlotId ?? "" },
      }),
    });

  const { mutateAsync: createInvoiceMutation, isPending: isCreatingInvoice } =
    useMutation({
      mutationFn: mutate(invoiceApi.createInvoice, {
        pathParams: { facilityId },
      }),
    });

  const { mutateAsync: confirmAppointmentMutation, isPending: isConfirming } =
    useMutation({
      mutationFn: mutate(scheduleApi.appointments.update, {
        pathParams: { facilityId, id: pendingBookingId! },
      }),
      onSuccess: async (data: Appointment) => {
        sessionStorage.removeItem(storageKey);
        setPendingBookingId(null);
        setPendingInvoiceId(null);
        setPendingEncounterId(null);
        setPendingExpiry(null);
        setSelectedSlotId(undefined);
        setSelectedSlot(null);

        toast.success(t("appointment_confirmed_successfully"));
        onSuccess?.();

        navigate(
          `/facility/${facilityId}/patient/${patientId}/appointments/${data.id}?showSuccess=true`,
        );
      },
      onError: () => {
        hasAutoConfirmedRef.current = false;
        toast.error(t("complete_payment_before_confirming_appointment"));
      },
    });

  useEffect(() => {
    if (
      !isBeforeAppointmentGate ||
      !isReservationActive ||
      !hasPendingInvoice ||
      !isPaid ||
      pendingAppointment?.status !== AppointmentStatus.PENDING ||
      hasAutoConfirmedRef.current
    ) {
      return;
    }

    hasAutoConfirmedRef.current = true;
    confirmAppointmentMutation({
      status: AppointmentStatus.BOOKED,
      note: pendingAppointment?.note ?? reason,
      encounter: pendingEncounterId,
    }).catch(() => {});
  }, [
    confirmAppointmentMutation,
    hasPendingInvoice,
    isBeforeAppointmentGate,
    isPaid,
    isReservationActive,
    pendingAppointment?.note,
    pendingAppointment?.status,
    pendingEncounterId,
    reason,
  ]);

  const { mutateAsync: cancelBookingMutation, isPending: isCancelling } =
    useMutation<
      AppointmentActionResponse | null,
      unknown,
      PendingCancelRequest
    >({
      mutationFn: (variables) => {
        const id = pendingBookingIdRef.current;
        if (!id) return Promise.resolve(null);
        return callApi(scheduleApi.appointments.cancel, {
          pathParams: { facilityId, id },
          body: variables,
        });
      },
      onSuccess: () => {
        clearReservation();
      },
    });

  // Event handlers
  const handleSlotSelect = (slotId: string | undefined) => {
    if (isSlotSelectionLocked) {
      return;
    }

    if (pendingBookingId && slotId !== selectedSlotId) {
      cancelBookingMutation({ reason: "cancelled" }).catch(() => {});
      clearReservation();
    }
    setSelectedSlotId(slotId);
  };

  const handleDateSelectionChange = (date: Date) => {
    if (isSlotSelectionLocked) {
      return;
    }

    if (pendingBookingId) {
      cancelBookingMutation({ reason: "cancelled" }).catch(() => {});
      clearReservation();
    }

    setSelectedDate(date);
  };

  const handleCreateInvoice = async () => {
    if (activeEncounters.length > 0) {
      setPendingBillingFlow(true);
      setShowEncounterDialog(true);
      return;
    }

    await handleStartBillingFlow(false);
  };

  const buildInvoiceUrl = (invoiceId: string, chargeItemId?: string) => {
    const params = new URLSearchParams();
    if (pendingReturnUrlRef.current) {
      params.set("sourceUrl", pendingReturnUrlRef.current);
    }
    if (chargeItemId) {
      params.set("editChargeItems", chargeItemId);
    }
    return `/facility/${facilityId}/billing/invoices/${invoiceId}${params.size > 0 ? `?${params.toString()}` : ""}`;
  };

  const buildCreateInvoiceUrl = (accountId: string, chargeItemId?: string) => {
    const params = new URLSearchParams();
    if (pendingReturnUrlRef.current) {
      params.set("sourceUrl", pendingReturnUrlRef.current);
    }
    if (chargeItemId) {
      params.set("preselectChargeItems", chargeItemId);
      params.set("editChargeItems", chargeItemId);
    }
    return `/facility/${facilityId}/billing/account/${accountId}/invoices/create${params.size > 0 ? `?${params.toString()}` : ""}`;
  };

  const handleStartBillingFlow = async (
    openDiscountEditor: boolean,
    encounterId: string | null = null,
  ) => {
    if (!isBeforeAppointmentGate || !selectedSlotId) return;
    // Capture the path+search (not the full href) so InvoiceShow's navigate(sourceUrl)
    // correctly returns to this page within the SPA.
    pendingReturnUrlRef.current =
      window.location.pathname + window.location.search;

    const appointment = await createAppointmentMutation({
      patient: patientId,
      note: reason,
      tags: selectedTags.map((tag) => tag.id),
      visit_type: selectedVisitType,
      patient_visit_type: selectedPatientVisitType,
      // Preserve the encounter selected in the link-existing-encounter dialog.
      // The paid booking flow previously dropped it while creating the pending
      // appointment, so a new encounter was created instead after confirmation.
      encounter: encounterId ?? undefined,
      service_request: selectedServiceRequestId,
    });

    if (appointment.status !== AppointmentStatus.PENDING) {
      sessionStorage.removeItem(storageKey);
      toast.success(t("appointment_created_successfully"));
      onSuccess?.();
      navigate(
        `/facility/${facilityId}/patient/${patientId}/appointments/${appointment.id}?showSuccess=true`,
      );
      return;
    }

    await redirectPendingAppointmentToBilling(
      appointment,
      openDiscountEditor,
      encounterId,
    );
  };

  const redirectPendingAppointmentToBilling = async (
    appointment: Appointment,
    openDiscountEditor = false,
    encounterId: string | null = null,
  ) => {
    pendingReturnUrlRef.current =
      window.location.pathname + window.location.search;

    const chargeItemId = appointment.charge_item?.id;
    const accountId =
      appointment.charge_item?.account ?? (await resolveDefaultAccountId());

    if (chargeItemId && accountId) {
      try {
        const invoice = await createInvoiceMutation({
          account: accountId,
          charge_items: [chargeItemId],
          status: InvoiceStatus.draft,
        });
        if (!invoice.number) {
          savePendingState(appointment, invoice.id, accountId, encounterId);
          setPendingInvoiceId(invoice.id);
          setInvoiceNumberRetry({
            invoiceId: invoice.id,
            chargeItemId,
            openDiscountEditor,
          });
          return;
        }

        savePendingState(appointment, invoice.id, accountId, encounterId);
        navigate(
          buildInvoiceUrl(
            invoice.id,
            openDiscountEditor ? chargeItemId : undefined,
          ),
        );
        return;
      } catch {
        // Invoice creation failed — fall back to manual create invoice flow.
        savePendingState(appointment, null, accountId, encounterId);
        navigate(
          buildCreateInvoiceUrl(
            accountId,
            openDiscountEditor ? chargeItemId : undefined,
          ),
        );
        return;
      }
    }

    savePendingState(appointment, null, accountId, encounterId);
    navigate(
      `/facility/${facilityId}/billing/account?status=active&patient_filter=${patientId}`,
    );
  };

  const retryInvoiceNumberGeneration = () => {
    const retry = invoiceNumberRetry;
    setInvoiceNumberRetry(null);
    if (!retry) return;
    navigate(
      buildInvoiceUrl(
        retry.invoiceId,
        retry.openDiscountEditor ? retry.chargeItemId : undefined,
      ),
    );
  };

  const handleConfirmAppointment = () => {
    if (!pendingBookingId) return;
    const note = pendingAppointment?.note ?? reason;
    confirmAppointmentMutation({
      status: AppointmentStatus.BOOKED,
      note,
      encounter: pendingEncounterId,
    });
  };

  const handleBookAppointment = () => {
    if (activeEncounters.length > 0) {
      setShowEncounterDialog(true);
      return;
    }

    handleBookDirectly();
  };

  const handleBookDirectly = async () => {
    if (!selectedSlotId) return;

    if (
      isBeforeAppointmentGate &&
      selectedVisitType === AppointmentVisitType.OPD
    ) {
      if (consultationFee) {
        toast.error(t("complete_payment_before_confirming_appointment"));
        return;
      }
    }

    const appointment = await createAppointmentMutation({
      patient: patientId,
      note: reason,
      tags: selectedTags.map((tag) => tag.id),
      visit_type: selectedVisitType,
      patient_visit_type: selectedPatientVisitType,
      encounter: undefined,
      service_request: selectedServiceRequestId,
    });

    if (appointment.status === AppointmentStatus.PENDING) {
      await redirectPendingAppointmentToBilling(appointment);
      return;
    }

    sessionStorage.removeItem(storageKey);
    toast.success(t("appointment_created_successfully"));
    onSuccess?.();
    navigate(
      `/facility/${facilityId}/patient/${patientId}/appointments/${appointment.id}?showSuccess=true`,
    );
  };

  const handleDrawerOpenChange = (open: boolean) => {
    setIsDrawerOpen(open);
    if (!open) {
      setDrawerStep(1);
      if (!pendingBookingId) setSelectedSlotId(undefined);
    }
  };

  // Navigate to the existing draft invoice (if one was created) or the create-invoice
  // page, carrying the current path as sourceUrl for the back-navigation.
  const handleGoToBilling = async () => {
    await handleGoToBillingWithOptions(false);
  };

  const handleGoToBillingWithOptions = async (openDiscountEditor: boolean) => {
    const returnUrl = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    const existingInvoiceId =
      pendingInvoiceId ??
      pendingAppointment?.charge_item?.paid_invoice?.id ??
      persistedState?.invoiceId ??
      null;
    const chargeItemId = pendingAppointment?.charge_item?.id;
    if (existingInvoiceId) {
      navigate(
        `/facility/${facilityId}/billing/invoices/${existingInvoiceId}?sourceUrl=${returnUrl}${openDiscountEditor && chargeItemId ? `&editChargeItems=${encodeURIComponent(chargeItemId)}` : ""}`,
      );
      return;
    }

    const acctId =
      pendingAppointment?.charge_item?.account ??
      persistedState?.accountId ??
      defaultAccountId ??
      (await refetchDefaultAccount()).data?.results[0]?.id ??
      null;

    if (acctId) {
      navigate(
        `/facility/${facilityId}/billing/account/${acctId}/invoices/create?sourceUrl=${returnUrl}${openDiscountEditor && chargeItemId ? `&preselectChargeItems=${encodeURIComponent(chargeItemId)}&editChargeItems=${encodeURIComponent(chargeItemId)}` : ""}`,
      );
    } else {
      navigate(
        `/facility/${facilityId}/billing/account?status=active&patient_filter=${patientId}`,
      );
    }
  };

  // Consultation Fee Card
  const renderFeeCard = () => {
    if (!showFeeCard) return null;

    if (isResolvingOpdSlot) {
      return (
        <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900 dark:border-gray-700 flex flex-col gap-3 mt-3">
          <div className="flex items-center gap-2">
            <CreditCard className="size-4 text-primary-600 shrink-0" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              {t("consultation_fee_required")}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t("loading")}
          </p>
        </div>
      );
    }

    const feeFromSlot = consultationFee;
    const feeFromAppointment =
      pendingAppointment?.charge_item?.charge_item_definition ?? null;
    const feeTitle =
      feeFromSlot?.title ??
      (feeFromAppointment && "title" in feeFromAppointment
        ? feeFromAppointment.title
        : t("consultation_fee_required"));
    const feeAmount =
      (feeFromSlot
        ? getBasePrice(feeFromSlot.price_components).toString()
        : null) ??
      (pendingAppointment?.charge_item?.total_price
        ? String(pendingAppointment.charge_item.total_price)
        : null);

    return (
      <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900 dark:border-gray-700 flex flex-col gap-3 mt-3">
        <div className="flex items-center gap-2">
          <CreditCard className="size-4 text-primary-600 shrink-0" />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            {t("consultation_fee_required")}
          </span>
          {isPaid && isReservationActive && (
            <span className="ml-auto text-xs font-medium text-green-600 flex items-center gap-1">
              <CheckCircle className="size-3" />
              {t("paid")}
            </span>
          )}
          {!isPaid && isReservationActive && timerLabel && (
            <span className="ml-auto text-xs font-medium text-amber-600 flex items-center gap-1">
              <Timer className="size-3" />
              {timerLabel}
            </span>
          )}
        </div>

        {feeAmount && (
          <div className="flex justify-between items-center bg-white dark:bg-gray-950 border dark:border-gray-700 rounded px-3 py-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {feeTitle}
            </span>
            <span
              className={`text-sm font-semibold ${isPaid && isReservationActive ? "text-green-600" : "text-gray-900 dark:text-gray-50"}`}
            >
              {getCurrencySymbol()}
              {feeAmount}
            </span>
          </div>
        )}

        {!pendingBookingId && !selectedSlotId && (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {t("select_slot_to_see_consultation_fee")}
          </p>
        )}

        <div className="flex flex-col gap-2">
          {!pendingBookingId && selectedSlotId && consultationFee && (
            <>
              <Button
                variant="primary"
                size="sm"
                className="w-full"
                onClick={handleCreateInvoice}
                disabled={isCreating || isCreatingInvoice}
              >
                <ExternalLink className="size-3.5 mr-1.5" />
                {t("create_invoice")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  if (activeEncounters.length > 0) {
                    setPendingBillingFlow(true);
                    setShowEncounterDialog(true);
                    return;
                  }

                  handleStartBillingFlow(true);
                }}
                disabled={isCreating || isCreatingInvoice}
              >
                {t("add_discount")}
              </Button>
            </>
          )}

          {isReservationActive && !isPaid && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleGoToBilling}
              >
                <ExternalLink className="size-3.5 mr-1.5" />
                {t("go_to_billing")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => handleGoToBillingWithOptions(true)}
              >
                {t("add_discount")}
              </Button>
            </>
          )}

          {isReservationActive && !hasPendingInvoice && (
            <Button
              variant="outline"
              size="sm"
              className="w-full text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => cancelBookingMutation({ reason: "cancelled" })}
              disabled={isCancelling}
            >
              {t("cancel_pending_booking")}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderServiceRequestSelector = () => {
    if (!requiresServiceRequestLink) return null;

    return (
      <div className="border rounded-lg p-4 bg-gray-50 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-4 text-primary-600 shrink-0" />
          <span className="text-sm font-semibold text-gray-900">
            {t("service_request_order")}
          </span>
          <span className="text-xs text-gray-500">({t("optional")})</span>
        </div>

        {isLoadingServiceRequests ? (
          <p className="text-sm text-gray-600">{t("loading")}</p>
        ) : eligibleServiceRequests.length === 0 ? (
          <p className="text-sm text-gray-600">
            {t("no_unlinked_service_requests")}
          </p>
        ) : (
          <Select
            value={selectedServiceRequestId ?? undefined}
            onValueChange={setSelectedServiceRequestId}
          >
            <SelectTrigger className="!h-auto min-h-[4.75rem] w-full items-start whitespace-normal bg-white px-3 py-2 text-left">
              {selectedServiceRequest ? (
                <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden pr-1">
                  <span className="truncate text-sm font-medium leading-5 text-gray-950">
                    {selectedServiceRequest.title}
                  </span>
                  <span className="line-clamp-2 text-xs leading-4 text-gray-600">
                    {selectedServiceRequest.code?.display ??
                      selectedServiceRequest.category}
                  </span>
                  <span className="flex flex-wrap gap-1 text-xs leading-4 text-gray-500">
                    <span>{selectedServiceRequest.status}</span>
                    <span>/</span>
                    <span>{selectedServiceRequest.priority}</span>
                  </span>
                </div>
              ) : (
                <SelectValue placeholder={t("select_service_request_order")} />
              )}
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
              {eligibleServiceRequests.map((serviceRequest) => (
                <SelectItem
                  key={serviceRequest.id}
                  value={serviceRequest.id}
                  textValue={serviceRequest.title}
                  className="items-start py-2"
                >
                  <div className="flex min-w-0 flex-col gap-1 pr-2">
                    <span className="whitespace-normal text-sm font-medium leading-5">
                      {serviceRequest.title}
                    </span>
                    <span className="whitespace-normal text-xs leading-4 text-gray-600">
                      {serviceRequest.code?.display ?? serviceRequest.category}{" "}
                      - {serviceRequest.status} / {serviceRequest.priority}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    );
  };

  // Action row below slot picker (desktop)
  const renderActionRow = () => {
    if (!selectedSlotId) return null;

    if (showFeeCard) {
      return (
        <div className="hidden sm:flex p-4 shadow mt-2">
          <div className="flex gap-4 ml-auto items-center">
            {!pendingBookingId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedSlotId(undefined);
                  setSelectedSlot(null);
                }}
              >
                {t("cancel")}
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirmAppointment}
              disabled={!canConfirm || isConfirming}
            >
              {t("confirm_appointment")}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="hidden sm:flex p-4 shadow mt-2">
        <div className="flex gap-4 ml-auto">
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => {
              setSelectedSlotId(undefined);
              setSelectedSlot(null);
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleBookAppointment}
            disabled={isCreating}
            type="submit"
          >
            {t("book_appointment")}
          </Button>
        </div>
      </div>
    );
  };

  const resourceIdForPicker =
    selectedResource.resource?.id ?? persistedState?.resourceId ?? undefined;

  const renderSlotSelectionLockMessage = () => {
    if (!isSlotSelectionLocked) {
      return null;
    }

    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        {t("slot_selection_locked_after_invoice_creation")}
      </div>
    );
  };

  return (
    <div className="w-full">
      <AlertDialog
        open={showEncounterDialog}
        onOpenChange={(open) => {
          setShowEncounterDialog(open);

          if (!open) {
            setPendingBillingFlow(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("active_encounter_found")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {t("active_encounter_found_description")}
                <div className="mt-4 space-y-3">
                  {activeEncounters.map((encounter) => (
                    <div
                      key={encounter.id}
                      onClick={() => setSelectedEncounter(encounter)}
                      className={`rounded-lg border p-3 cursor-pointer transition ${
                        selectedEncounter?.id === encounter.id
                          ? "border-primary bg-primary/5"
                          : "border-gray-200"
                      }`}
                    >
                      <div>
                        <strong>{t("visit_id")}:</strong>{" "}
                        {encounter.external_identifier}
                      </div>

                      <div>
                        <strong>{t("encounter")}:</strong>{" "}
                        {encounter.encounter_class}
                      </div>

                      <div>
                        <strong>{t("status")}:</strong> {encounter.status}
                      </div>

                      <div>
                        <strong>{t("started")}:</strong>{" "}
                        {encounter.period?.start}
                      </div>

                      <div>
                        <strong>{t("facility")}:</strong>{" "}
                        {encounter.facility?.name}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4">{t("link_appointment_confirmation")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEncounterDialog(false);
                setPendingBillingFlow(false);
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowEncounterDialog(false);

                if (pendingBillingFlow) {
                  setPendingBillingFlow(false);
                  handleStartBillingFlow(false);
                  return;
                }

                handleBookDirectly();
              }}
            >
              {t("continue_separately")}
            </Button>
            <Button
              onClick={async () => {
                if (!selectedEncounter) {
                  if (pendingBillingFlow) {
                    setPendingBillingFlow(false);
                  }

                  toast.error(t("please_select_a_encounter"));
                  return;
                }

                setShowEncounterDialog(false);

                // OPD payment flow
                if (
                  pendingBillingFlow &&
                  isBeforeAppointmentGate &&
                  selectedVisitType === AppointmentVisitType.OPD
                ) {
                  setPendingBillingFlow(false);

                  await handleStartBillingFlow(false, selectedEncounter.id);

                  return;
                }

                try {
                  const appointment = await createAppointmentMutation({
                    patient: patientId,
                    note: reason,
                    tags: selectedTags.map((tag) => tag.id),
                    visit_type: selectedVisitType,
                    patient_visit_type: selectedPatientVisitType,
                    encounter: selectedEncounter.id,
                    service_request: selectedServiceRequestId,
                  });

                  if (appointment.status === AppointmentStatus.PENDING) {
                    await redirectPendingAppointmentToBilling(
                      appointment,
                      false,
                      selectedEncounter.id,
                    );
                    return;
                  }

                  toast.success(t("appointment_linked_successfully"));

                  onSuccess?.();

                  navigate(
                    `/facility/${facilityId}/patient/${patientId}/appointments/${appointment.id}?showSuccess=true`,
                  );
                } catch {
                  toast.error(t("failed_to_link_appointment"));
                }
              }}
            >
              {t("link_encounter")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!invoiceNumberRetry}
        onOpenChange={(open) => {
          if (!open) setInvoiceNumberRetry(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("failed_to_create_invoice")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("invoice_number_not_generated")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={retryInvoiceNumberGeneration}>
              {t("try_again")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex justify-center gap-4 md:flex-col lg:flex-row">
        {/* Left panel: form fields */}
        <div className="flex w-full max-w-md flex-col gap-8 rounded-lg bg-white p-4 dark:bg-gray-900 shadow">
          <AppointmentFormSection
            facilityId={facilityId}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            reason={reason}
            setReason={setReason}
            selectedVisitType={selectedVisitType}
            setSelectedVisitType={(v) => {
              if (v !== AppointmentVisitType.OPD && pendingBookingId) {
                cancelBookingMutation({ reason: "cancelled" }).catch(() => {});
                clearReservation();
              }
              setSelectedVisitType(v);
            }}
            selectedPatientVisitType={selectedPatientVisitType}
            setSelectedPatientVisitType={setSelectedPatientVisitType}
            selectedResource={selectedResource}
            setSelectedResource={(r) => {
              if (pendingBookingId) {
                cancelBookingMutation({ reason: "cancelled" }).catch(() => {});
                clearReservation();
              }
              setSelectedServiceRequestId(null);
              setSelectedResource(r);
            }}
          />
          {renderServiceRequestSelector()}
        </div>

        {/* Right panel: date + slot picker + fee card */}
        <div className="hidden sm:flex sm:flex-col lg:flex-col xl:flex-row gap-6 bg-white dark:bg-gray-900 shadow rounded-lg p-4 w-full sm:max-h-full">
          <div className="flex flex-col gap-3 w-full ">
            {renderSlotSelectionLockMessage()}
            <AppointmentDateSelection
              facilityId={facilityId}
              resourceId={resourceIdForPicker}
              resourceType={selectedResource.resource_type}
              setSelectedDate={handleDateSelectionChange}
              selectedDate={selectedDate}
              disabled={isSlotSelectionLocked}
            />
          </div>
          <div className="w-full overflow-y-auto lg:max-h-[calc(100vh-17rem)] md:*:max-h-[calc(100vh-5rem)] ">
            <AppointmentSlotPicker
              facilityId={facilityId}
              resourceId={resourceIdForPicker}
              resourceType={selectedResource.resource_type}
              selectedSlotId={selectedSlotId}
              onSlotSelect={handleSlotSelect}
              onSlotDetailsChange={(slot) => {
                setSelectedSlot(slot);
                if (pendingBookingId && slot.id !== selectedSlotId) {
                  cancelBookingMutation({ reason: "cancelled" }).catch(
                    () => {},
                  );
                  clearReservation();
                }
              }}
              selectedDate={selectedDate}
              pinnedSlot={pendingBookingId ? selectedSlot : null}
              reservedUntil={pendingExpiry}
              disabled={isSlotSelectionLocked}
            />
            {renderFeeCard()}
          </div>
        </div>
      </div>

      {renderActionRow()}

      {/* Mobile drawer */}
      <Drawer open={isDrawerOpen} onOpenChange={handleDrawerOpenChange}>
        <DrawerTrigger asChild>
          <Button
            className="sm:hidden w-full mt-3"
            disabled={!resourceIdForPicker}
            onClick={() => {
              setIsDrawerOpen(true);
              setDrawerStep(1);
            }}
          >
            {t("select_date")}
            <ArrowRight size={16} />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="w-full p-4 space-y-4">
          <div className="flex flex-col gap-3 overflow-y-auto -mx-2">
            {drawerStep === 1 && (
              <>
                {renderSlotSelectionLockMessage()}
                <AppointmentDateSelection
                  facilityId={facilityId}
                  resourceId={resourceIdForPicker}
                  resourceType={selectedResource.resource_type}
                  setSelectedDate={handleDateSelectionChange}
                  selectedDate={selectedDate}
                  disabled={isSlotSelectionLocked}
                />
                <Button
                  className="w-full"
                  disabled={!selectedDate}
                  onClick={() => setDrawerStep(2)}
                >
                  {t("select_slot")}
                  <ArrowRight size={16} />
                </Button>
              </>
            )}
          </div>

          {drawerStep === 2 && (
            <>
              {renderSlotSelectionLockMessage()}
              <AppointmentSlotPicker
                facilityId={facilityId}
                resourceId={resourceIdForPicker}
                resourceType={selectedResource.resource_type}
                selectedSlotId={selectedSlotId}
                onSlotSelect={handleSlotSelect}
                onSlotDetailsChange={(slot) => setSelectedSlot(slot)}
                selectedDate={selectedDate}
                pinnedSlot={pendingBookingId ? selectedSlot : null}
                reservedUntil={pendingExpiry}
                disabled={isSlotSelectionLocked}
              />
              {renderFeeCard()}
              <div className="sm:hidden flex flex-row gap-2 items-center justify-around">
                <Button
                  variant="outline"
                  className="w-fit"
                  onClick={() => {
                    setDrawerStep(1);
                    if (!pendingBookingId) setSelectedSlotId(undefined);
                  }}
                >
                  <ArrowLeft />
                  {t("back")}
                </Button>
                {showFeeCard ? (
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleConfirmAppointment}
                    disabled={!canConfirm || isConfirming}
                  >
                    {t("confirm_appointment")}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleBookDirectly}
                    disabled={!selectedSlotId || isCreating}
                  >
                    {t("book_appointment")}
                  </Button>
                )}
              </div>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
};
